import {
  findDuplicateChatListGroupLabel,
  validateChatListGroupLabels,
} from './chatListGroups';

export { slugifyGroupId, validateChatListGroupLabels, findDuplicateChatListGroupLabel } from './chatListGroups';

export const ARTICLE_IMPORTANCE = {
  ESSENTIAL: 'essential',
  NORMAL: 'normal',
  ARCHIVE: 'archive',
};

export const MAX_ESSENTIAL_ARTICLES = 3;

export const ARTICLE_ESSENTIAL_SECTION_TITLE = 'Aktualno';

export const ARTICLE_ESSENTIAL_SECTION_SUBTITLE =
  'Obrazci za podpis v tekoči akciji ali pogajanjih. Največ 3 naenkrat.';

export const ARTICLE_OTHER_SECTION_SUBTITLE =
  'Ostali obrazci in vloge, ki jih lahko člani kadarkoli prenesejo.';

export const ARTICLE_ARCHIVE_SECTION_SUBTITLE =
  'Starejši obrazci, ki niso več na glavnem seznamu.';

/** Select value for «Aktualno» (maps to importance essential). */
export const ARTICLE_SKUPINA_ESSENTIAL = '__essential__';

export const ARTICLE_SKUPINA_FIELD_HELP =
  'Kje naj bo obrazec na seznamu. Sprememba se shrani takoj. «Aktualno»: največ 3 obrazci na vrhu.';

export const ARTICLE_IMPORTANCE_OPTIONS = [
  { value: ARTICLE_IMPORTANCE.NORMAL, label: 'Navadno' },
  { value: ARTICLE_IMPORTANCE.ESSENTIAL, label: 'Aktualno' },
  { value: ARTICLE_IMPORTANCE.ARCHIVE, label: 'Arhiv' },
];

/** @deprecated Legacy section ids — resolved to `other` when grouping. */
export const LEGACY_ARTICLE_LIST_GROUP_IDS = new Set([
  'membership',
  'reimbursements',
  'leave',
]);

export const DEFAULT_ARTICLE_LIST_GROUPS = [
  { id: 'other', label: 'Pomembni obrazci', order: 0 },
  { id: 'archive', label: 'Arhiv', order: 1 },
];

export function isDefaultArticleListGroupId(groupId) {
  return DEFAULT_ARTICLE_LIST_GROUPS.some((group) => group.id === groupId);
}

export function isArticleArchiveGroupId(groupId) {
  return groupId === 'archive';
}

export function isArticleEssentialDisplayGroupId(groupId) {
  return groupId === ARTICLE_SKUPINA_ESSENTIAL;
}

function normalizeArticleGroupLabel(label) {
  return String(label || '').trim().toLowerCase();
}

/** Legacy/custom ids that must map to the system Aktualno section. */
export function isArticleEssentialSkupinaValue(skupinaValue) {
  const normalized = String(skupinaValue || '').trim().toLowerCase();
  return skupinaValue === ARTICLE_SKUPINA_ESSENTIAL
    || normalized === 'essential'
    || normalized === 'aktualno';
}

/** Custom tenant groups must not duplicate the system Aktualno section. */
export function isReservedArticleCustomGroup(group) {
  if (!group?.id) {
    return true;
  }

  const id = String(group.id).trim().toLowerCase();
  if (isArticleEssentialSkupinaValue(id)) {
    return true;
  }

  const label = normalizeArticleGroupLabel(group.label || group.id);
  return label === normalizeArticleGroupLabel(ARTICLE_ESSENTIAL_SECTION_TITLE);
}

/** Rows for admin «Skupine obrazcev» table — essential first, archive last. */
export function buildArticleGroupManagementRows(tenantConfig) {
  const resolved = resolveArticleListGroups(tenantConfig);
  const other = resolved.find((group) => group.id === 'other')
    || { id: 'other', label: 'Pomembni obrazci', order: 1 };
  const archive = resolved.find((group) => group.id === 'archive')
    || { id: 'archive', label: 'Arhiv', order: 999 };
  const custom = resolved
    .filter((group) => group.id !== 'other' && group.id !== 'archive')
    .sort((a, b) => {
      if (a.order !== b.order) return a.order - b.order;
      return a.label.localeCompare(b.label, 'sl');
    });

  const editable = [other, ...custom].map((group, index) => ({
    ...group,
    order: index + 1,
  }));

  const archiveOrder = editable.length + 1;

  return {
    essentialRow: {
      id: ARTICLE_SKUPINA_ESSENTIAL,
      label: ARTICLE_ESSENTIAL_SECTION_TITLE,
      order: 0,
      description: resolveArticleEssentialSectionSubtitle(tenantConfig),
    },
    editableGroups: editable,
    archiveRow: {
      ...archive,
      order: archiveOrder,
    },
  };
}

export function resolveArticleEssentialSectionSubtitle(tenantConfig) {
  const stored = tenantConfig?.articleEssentialSectionSubtitle;
  if (typeof stored === 'string' && stored.trim()) {
    return stored.trim();
  }
  return ARTICLE_ESSENTIAL_SECTION_SUBTITLE;
}

export function resolveArticleListGroupDescription(group) {
  if (typeof group?.description === 'string' && group.description.trim()) {
    return group.description.trim();
  }
  return null;
}

export function resolveArticleListGroupSubtitle(group) {
  const custom = resolveArticleListGroupDescription(group);
  if (custom) return custom;
  if (group?.id === 'other') return ARTICLE_OTHER_SECTION_SUBTITLE;
  if (group?.id === 'archive') return ARTICLE_ARCHIVE_SECTION_SUBTITLE;
  return undefined;
}

export function normalizeArticleListGroupsForSave(editableGroups) {
  return [...editableGroups]
    .sort((a, b) => {
      const orderA = typeof a.order === 'number' ? a.order : 999;
      const orderB = typeof b.order === 'number' ? b.order : 999;
      if (orderA !== orderB) return orderA - orderB;
      return (a.label || '').localeCompare(b.label || '', 'sl');
    })
    .map((group, index) => {
      const entry = {
        id: group.id,
        label: String(group.label || '').trim() || group.id,
        order: index + 1,
      };
      const description = String(group.description || '').trim();
      if (description) {
        entry.description = description;
      }
      return entry;
    })
    .filter((group) => group.id && !isArticleArchiveGroupId(group.id));
}

export function buildArticleListGroupsSavePayload({ editableGroups, archiveRow }) {
  const normalized = normalizeArticleListGroupsForSave(editableGroups);
  const archiveEntry = {
    id: 'archive',
    label: String(archiveRow?.label || 'Arhiv').trim() || 'Arhiv',
    order: normalized.length + 1,
  };
  const archiveDescription = String(archiveRow?.description || '').trim();
  if (archiveDescription) {
    archiveEntry.description = archiveDescription;
  }
  return [...normalized, archiveEntry];
}

export function resolveArticleListGroups(tenantConfig) {
  const custom = tenantConfig?.articleListGroups;
  const byId = new Map(
    DEFAULT_ARTICLE_LIST_GROUPS.map((group) => [group.id, { ...group }]),
  );

  if (Array.isArray(custom)) {
    custom.forEach((group) => {
      if (!group?.id || isReservedArticleCustomGroup(group)) {
        return;
      }

      const label = group.label || group.id;
      const duplicateLabel = findDuplicateChatListGroupLabel(
        Array.from(byId.values()),
        label,
        group.id,
      );
      if (duplicateLabel) {
        return;
      }

      byId.set(group.id, {
        id: group.id,
        label,
        order: typeof group.order === 'number' ? group.order : 999,
        ...(typeof group.description === 'string' && group.description.trim()
          ? { description: group.description.trim() }
          : {}),
      });
    });
  }

  return Array.from(byId.values()).sort((a, b) => {
    if (a.id === 'archive') return 1;
    if (b.id === 'archive') return -1;
    if (a.order !== b.order) return a.order - b.order;
    return a.label.localeCompare(b.label, 'sl');
  });
}

export function resolveArticleImportance(article) {
  const raw = article?.importance;
  if (
    raw === ARTICLE_IMPORTANCE.ESSENTIAL
    || raw === ARTICLE_IMPORTANCE.ARCHIVE
    || raw === ARTICLE_IMPORTANCE.NORMAL
  ) {
    return raw;
  }

  const groupId = String(article?.listGroupId || '').trim().toLowerCase();
  if (
    groupId === ARTICLE_SKUPINA_ESSENTIAL
    || groupId === 'essential'
    || groupId === 'aktualno'
  ) {
    return ARTICLE_IMPORTANCE.ESSENTIAL;
  }

  return ARTICLE_IMPORTANCE.NORMAL;
}

export function normalizeArticleRecord(article) {
  const importance = resolveArticleImportance(article);
  let listGroupId = article?.listGroupId || 'other';

  if (importance === ARTICLE_IMPORTANCE.ARCHIVE) {
    listGroupId = 'archive';
  } else if (importance === ARTICLE_IMPORTANCE.ESSENTIAL) {
    const normalizedGroupId = String(listGroupId).trim().toLowerCase();
    if (
      !listGroupId
      || listGroupId === 'archive'
      || normalizedGroupId === ARTICLE_SKUPINA_ESSENTIAL
      || normalizedGroupId === 'essential'
      || normalizedGroupId === 'aktualno'
    ) {
      listGroupId = 'other';
    }
  } else {
    const normalizedGroupId = String(listGroupId).trim().toLowerCase();
    if (
      normalizedGroupId === ARTICLE_SKUPINA_ESSENTIAL
      || normalizedGroupId === 'essential'
      || normalizedGroupId === 'aktualno'
    ) {
      listGroupId = 'other';
    }
  }

  return {
    ...article,
    importance,
    listGroupId,
  };
}

export function resolveArticleListGroupId(article) {
  const normalized = normalizeArticleRecord(article);
  if (normalized.importance === ARTICLE_IMPORTANCE.ARCHIVE) {
    return 'archive';
  }
  const groupId = normalized.listGroupId || 'other';
  if (LEGACY_ARTICLE_LIST_GROUP_IDS.has(groupId)) {
    return 'other';
  }
  return groupId;
}

export function buildArticleSkupinaOptions(tenantConfig) {
  const groups = resolveArticleListGroups(tenantConfig).filter(
    (group) => group.id !== 'archive',
  );

  return [
    { value: ARTICLE_SKUPINA_ESSENTIAL, label: ARTICLE_ESSENTIAL_SECTION_TITLE },
    ...groups.map((group) => ({ value: group.id, label: group.label })),
    { value: 'archive', label: 'Arhiv' },
  ];
}

export function articleToSkupinaValue(article) {
  const normalized = normalizeArticleRecord(article);
  if (normalized.importance === ARTICLE_IMPORTANCE.ESSENTIAL) {
    return ARTICLE_SKUPINA_ESSENTIAL;
  }
  if (normalized.importance === ARTICLE_IMPORTANCE.ARCHIVE) {
    return 'archive';
  }
  return resolveArticleListGroupId(normalized);
}

export function skupinaValueToGrouping(skupinaValue, article = {}) {
  let previousGroupId = article.listGroupId === 'archive'
    ? 'other'
    : (article.listGroupId || 'other');

  if (isArticleEssentialSkupinaValue(previousGroupId)) {
    previousGroupId = 'other';
  }

  if (isArticleEssentialSkupinaValue(skupinaValue)) {
    return {
      importance: ARTICLE_IMPORTANCE.ESSENTIAL,
      listGroupId: previousGroupId,
    };
  }

  if (skupinaValue === 'archive') {
    return {
      importance: ARTICLE_IMPORTANCE.ARCHIVE,
      listGroupId: 'archive',
    };
  }

  return {
    importance: ARTICLE_IMPORTANCE.NORMAL,
    listGroupId: skupinaValue || 'other',
  };
}

export function articleImportanceLabel(importance) {
  if (importance === ARTICLE_IMPORTANCE.ESSENTIAL) {
    return ARTICLE_ESSENTIAL_SECTION_TITLE;
  }
  if (importance === ARTICLE_IMPORTANCE.ARCHIVE) {
    return 'Arhiv';
  }
  return 'Navadno';
}

/** Human-readable placement for admin tables (status vs section). */
export function describeArticleListPlacement(article, tenantConfig) {
  const normalized = normalizeArticleRecord(article);
  if (normalized.importance === ARTICLE_IMPORTANCE.ESSENTIAL) {
    return ARTICLE_ESSENTIAL_SECTION_TITLE;
  }
  if (normalized.importance === ARTICLE_IMPORTANCE.ARCHIVE) {
    return 'Arhiv';
  }
  const groupId = resolveArticleListGroupId(normalized);
  return resolveArticleListGroups(tenantConfig)
    .find((group) => group.id === groupId)?.label || groupId;
}

export function sortArticlesInGroup(articles) {
  return [...articles].sort((a, b) => {
    const orderA = typeof a.order === 'number' ? a.order : 999;
    const orderB = typeof b.order === 'number' ? b.order : 999;
    if (orderA !== orderB) return orderA - orderB;
    return (a.title || '').localeCompare(b.title || '', 'sl');
  });
}

/** Grid columns for middle skupine: 2→2, 3→3, 4+→2 (2×2, …). */
export function resolveArticleMiddleGridColumns(sectionCount) {
  if (sectionCount <= 1) return 1;
  if (sectionCount === 2) return 2;
  if (sectionCount === 3) return 3;
  return 2;
}

export function groupArticles(articles, tenantConfig) {
  const groups = resolveArticleListGroups(tenantConfig);
  const normalizedArticles = articles.map(normalizeArticleRecord);
  const essential = sortArticlesInGroup(
    normalizedArticles.filter((item) => item.importance === ARTICLE_IMPORTANCE.ESSENTIAL),
  );
  const essentialIds = new Set(essential.map((item) => item.id));

  const groupMap = new Map(groups.map((group) => [group.id, []]));
  const fallbackGroupId = groupMap.has('other') ? 'other' : groups[0]?.id;

  normalizedArticles.forEach((article) => {
    if (essentialIds.has(article.id)) {
      return;
    }

    const groupId = resolveArticleListGroupId(article);
    const targetId = groupMap.has(groupId) ? groupId : fallbackGroupId;
    if (targetId) {
      groupMap.get(targetId).push(article);
    }
  });

  const sections = groups
    .filter((group) => (groupMap.get(group.id)?.length || 0) > 0)
    .map((group) => ({
      group,
      articles: sortArticlesInGroup(groupMap.get(group.id)),
    }));

  return { essential, sections };
}
